// Alaska clinics sign logo-free consent forms
// All the Alaska specifics live here, new-intake.component just delegates

// Clinic codes that get the Alaska files
export const UNBRANDED_CLINICS = [
	"AK01HF", // Huffman
	"AK02AE", // Anchorage East
	"AK03DM", // Dimond
	"AK04AS", // Anchorage South
	"AK05ER", // Eagle River
	"ZZ99ZZ", // test clinic
];

export function isUnbrandedClinic(clinicCode: string | null | undefined): boolean {
	if (!clinicCode) return false;
	return UNBRANDED_CLINICS.includes(clinicCode);
}

// Branded template -> Alaska copy in src/assets/unbranded
// Legacy flow templates have no Alaska version
const UNBRANDED_TEMPLATES: Record<string, string> = {
	"assets/informed_consent_template2.pdf": "assets/unbranded/informed_consent_template2.pdf",
	"assets/agreements_template2.pdf": "assets/unbranded/agreements_template2.pdf",
	"assets/scribe_template_new.pdf": "assets/unbranded/scribe_template_new.pdf",
	"assets/receipt_of_nopp.pdf": "assets/unbranded/receipt_of_nopp.pdf",
	// Appended whole, nothing is drawn on it, so it has no layout entry
	"assets/nopp.pdf": "assets/unbranded/nopp.pdf",
	// Personal injury, shown in section 9 and its own tab when insurance is MVA
	"assets/pi_template.pdf": "assets/unbranded/pi_template.pdf",
};

export interface LayoutRemap {
	from: { x: number; y: number };
	to: { x: number; y: number };
}

// Where to draw on the Alaska templates, kept out of the render* methods
// Keyed by the branded x and y, anything unlisted stays where it is
const UNBRANDED_LAYOUT: Record<string, LayoutRemap[]> = {
	// Signature rules 405 -> 382 and 356 -> 333, date column 455 -> 468
	"assets/unbranded/informed_consent_template2.pdf": [
		{ from: { x: 80, y: 410 }, to: { x: 80, y: 387 } },
		{ from: { x: 455, y: 410 }, to: { x: 468, y: 387 } },
		{ from: { x: 80, y: 360 }, to: { x: 80, y: 337 } },
		{ from: { x: 455, y: 360 }, to: { x: 468, y: 337 } },
	],
	// Rules match the branded template, only the date moved
	"assets/unbranded/agreements_template2.pdf": [{ from: { x: 470, y: 60 }, to: { x: 489, y: 60 } }],
	"assets/unbranded/receipt_of_nopp.pdf": [{ from: { x: 430, y: 425 }, to: { x: 450, y: 425 } }],
	// Signature block moved down 12pt so the Alaska wording stops colliding with it
	// y 368 is the IsNewScribeConsent row
	"assets/unbranded/scribe_template_new.pdf": [
		{ from: { x: 80, y: 368 }, to: { x: 80, y: 356 } },
		{ from: { x: 430, y: 368 }, to: { x: 450, y: 356 } },
	],
	// Name, signature and date row sits 5pt lower, columns are unchanged
	"assets/unbranded/pi_template.pdf": [
		{ from: { x: 110, y: 120 }, to: { x: 110, y: 115 } },
		{ from: { x: 330, y: 120 }, to: { x: 330, y: 115 } },
		{ from: { x: 500, y: 120 }, to: { x: 500, y: 115 } },
	],
};

// ABN is not in the map and passes through
export function unbrandedTemplatePath(clinicCode: string, pdfPath: string): string {
	if (!isUnbrandedClinic(clinicCode)) return pdfPath;
	return UNBRANDED_TEMPLATES[pdfPath] ?? pdfPath;
}

export function unbrandedTemplateLayout(templatePath: string): LayoutRemap[] | undefined {
	return UNBRANDED_LAYOUT[templatePath];
}

// Signature image height where the default 30pt is too tall
// On PI the name, signature and date share one row with only 30pt of clear space
// above it, so a full height signature runs into the paragraph
const UNBRANDED_SIGNATURE_HEIGHT: Record<string, number> = {
	"assets/unbranded/pi_template.pdf": 18,
};

export function unbrandedSignatureHeight(templatePath: string): number | undefined {
	return UNBRANDED_SIGNATURE_HEIGHT[templatePath];
}

// Blob folder for the read-only PDFs the consent checkbox links to
const CONSENT_PDF_BASE = "https://medformioprodeastus2.blob.core.windows.net/patient-pdf/pdf";

// Blob files with an Alaska copy in pdf/unbrandedPDFs
// Checked per file, the label also links legacy forms that have none
const UNBRANDED_BLOB_PDFS = new Set([
	"Patient Informed Consent v3-2026-01-08.pdf",
	"Payment Agreement and Authorization v3-2026-01-08.pdf",
	"State Biometric Privacy Laws Disclosure and Consent New.pdf",
	"Acknowledgement of Receipt of NOPP v1-2026-01-08.pdf",
	"Notice of Privacy Practices v2-2026-01-08.pdf",
	"Personal Injury Authorization, Assignment, Contractual Lien & Letter of Protection.pdf",
	"Workers Comp (WC) Paperwork 2025.pdf",
]);

// Filenames behind Form.io labels that hardcode a blob link
export const PI_CONSENT_PDF =
	"Personal Injury Authorization, Assignment, Contractual Lien & Letter of Protection.pdf";
export const WC_FORMS_PDF = "Workers Comp (WC) Paperwork 2025.pdf";

// Alaska copies keep the branded filename
export function unbrandedConsentPdfUrl(clinicCode: string, fileName: string): string {
	const base =
		isUnbrandedClinic(clinicCode) && UNBRANDED_BLOB_PDFS.has(fileName)
			? `${CONSENT_PDF_BASE}/unbrandedPDFs`
			: CONSENT_PDF_BASE;
	return `${base}/${fileName}`;
}
